/**
 * Reviews.io rating snippets (product cards and the product page "Rating Output" app block).
 *
 * The Reviews.io "Rating Snippet" app embed renders every `.ruk_rating_snippet` element that exists shortly after
 * page load. This script complements it:
 *
 * 1. Snippets inserted later (collection filtering, "load more", recently viewed products, search results) are
 *    handed to Reviews.io again.
 * 2. Rendered snippets are reformatted to "4,9 (15)" (Figma: rating followed by the review count in parentheses)
 *    and the product card link around them is revealed. Products without reviews never render, so their link
 *    stays hidden.
 */
(function () {
  const SNIPPET_SELECTOR = '.ruk_rating_snippet';
  const CARD_WRAPPER_SELECTOR = '.product-card__rating';
  // The app embed retries for about four seconds after load before giving up
  const APP_EMBED_GRACE_PERIOD = 5000;

  const locale = document.documentElement.lang || 'de';
  let ratingFormatter = null;

  try {
    ratingFormatter = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  } catch (error) {
    ratingFormatter = null;
  }

  let loadedAt = document.readyState === 'complete' ? Date.now() : null;
  let initTimeout = null;

  function formatRating(rating) {
    return ratingFormatter ? ratingFormatter.format(rating) : rating.toFixed(1);
  }

  /**
   * Rewrites the snippet text (e.g. "(15 Bewertungen)") to "4,9 (15)". Rating and count are read from the
   * aria-label Reviews.io sets ("4.87 Stars - 15 Bewertungen"), the only place exposing the exact average.
   */
  function formatSnippet(snippet) {
    const label = snippet.getAttribute('aria-label') || snippet.getAttribute('title') || '';
    const match = label.match(/^\s*(\d+(?:\.\d+)?)[^-]*-\s*([\d.,]+)/);
    const countElements = snippet.querySelectorAll('.ruk-rating-snippet-count');

    if (!match || countElements.length === 0) {
      return;
    }

    const rating = parseFloat(match[1]);

    if (Number.isNaN(rating)) {
      return;
    }

    const text = `${formatRating(rating)} (${match[2]})`;

    if (countElements[0].textContent !== text) {
      countElements[0].textContent = text;
    }

    for (let index = 1; index < countElements.length; index += 1) {
      countElements[index].remove();
    }
  }

  function onSnippetRendered(snippet) {
    formatSnippet(snippet);

    const wrapper = snippet.closest(CARD_WRAPPER_SELECTOR);

    if (wrapper) {
      wrapper.removeAttribute('hidden');
    }
  }

  /** Asks Reviews.io to render the snippets that were added after its initial pass */
  function initialisePendingSnippets() {
    initTimeout = null;

    if (!document.querySelector(`${SNIPPET_SELECTOR}:not([data-done]):not([data-pending])`)) {
      return;
    }

    if (typeof window.ratingSnippet !== 'function') {
      return;
    }

    if (window.ratingSnippetGlobalSettings && typeof window.reinitialiseRatingSnippet === 'function') {
      window.reinitialiseRatingSnippet();
      return;
    }

    // The app embed has not initialised yet: leave the initial render to it while it is still retrying
    const waitFor = (loadedAt || Date.now()) + APP_EMBED_GRACE_PERIOD - Date.now();

    if (waitFor > 0) {
      initTimeout = setTimeout(initialisePendingSnippets, waitFor);
      return;
    }

    // The app embed only initialises snippets that exist shortly after load. For pages whose products are fetched
    // later, start Reviews.io with the store exposed by the "Reviews.io Core" app embed.
    if (typeof window.reviewsIoStore === 'string' && window.reviewsIoStore !== '') {
      window.ratingSnippet('ruk_rating_snippet', {
        store: window.reviewsIoStore,
        lang: window.reviewsIoLang || locale.split('-')[0],
        showEmptyStars: false,
        snippetPopup: false
      });
    }
  }

  function scheduleInitialisation() {
    if (loadedAt === null) {
      // Reviews.io's app embed renders the snippets present at page load itself
      return;
    }

    if (initTimeout) {
      clearTimeout(initTimeout);
    }

    initTimeout = setTimeout(initialisePendingSnippets, 150);
  }

  /** Handles the snippets inside a newly added node; returns true when some of them still need rendering */
  function scanNode(node) {
    if (!(node instanceof Element)) {
      return false;
    }

    const snippets = node.matches(SNIPPET_SELECTOR) ? [node] : Array.from(node.querySelectorAll(SNIPPET_SELECTOR));
    let pending = false;

    snippets.forEach((snippet) => {
      if (snippet.hasAttribute('data-done')) {
        onSnippetRendered(snippet);
      } else {
        pending = true;
      }
    });

    return pending;
  }

  const observer = new MutationObserver((mutations) => {
    let pending = false;

    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        if (mutation.target.hasAttribute('data-done')) {
          onSnippetRendered(mutation.target);
        }
      } else {
        mutation.addedNodes.forEach((node) => {
          if (scanNode(node)) {
            pending = true;
          }
        });
      }
    });

    if (pending) {
      scheduleInitialisation();
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-done']
  });

  if (scanNode(document.documentElement)) {
    scheduleInitialisation();
  }

  if (loadedAt === null) {
    window.addEventListener('load', () => {
      loadedAt = Date.now();
      scheduleInitialisation();
    });
  }
})();
